import React, { useState, useEffect } from 'react';
import { Layout, Space, Typography, Tag, Button, Modal, Input, message } from 'antd';
import { UserOutlined, LoginOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { STORAGE_KEYS } from '../lib/constants';
import { getApiUrl } from '../lib/config';

const { Header } = Layout;
const { Text } = Typography;

export const AppHeader: React.FC = () => {
  const navigate = useNavigate();
  const [playerId, setPlayerId] = useState<string | null>(null);
  const [playerName, setPlayerName] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [inputName, setInputName] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    // Load player info from localStorage
    const storedId = localStorage.getItem(STORAGE_KEYS.PLAYER_ID);
    const storedName = localStorage.getItem(STORAGE_KEYS.PLAYER_NAME);
    setPlayerId(storedId);
    setPlayerName(storedName);

    // If no player, show modal
    if (!storedId) {
      setIsModalOpen(true);
    }
  }, []);

  const handleCreatePlayer = async () => {
    if (!inputName.trim()) {
      message.error('Please enter a display name');
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(`${getApiUrl()}/players`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ displayName: inputName.trim() }),
      });

      if (!response.ok) {
        throw new Error('Failed to create player');
      }

      const player = await response.json();

      // Store in localStorage
      localStorage.setItem(STORAGE_KEYS.PLAYER_ID, player.id);
      localStorage.setItem(STORAGE_KEYS.PLAYER_NAME, player.displayName);

      // Update state
      setPlayerId(player.id);
      setPlayerName(player.displayName);
      setIsModalOpen(false);

      message.success(`Welcome, ${player.displayName}!`);

      // Reload to ensure all components pick up the new player
      window.location.reload();
    } catch (error) {
      message.error('Failed to create player. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem(STORAGE_KEYS.PLAYER_ID);
    localStorage.removeItem(STORAGE_KEYS.PLAYER_NAME);
    localStorage.removeItem(STORAGE_KEYS.CURRENT_GAME_ID);
    localStorage.removeItem(STORAGE_KEYS.ASSIGNED_SIDE);
    setPlayerId(null);
    setPlayerName(null);
    setIsModalOpen(true);
  };

  return (
    <>
      <Header
        style={{
          background: '#fff',
          padding: '0 24px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid #f0f0f0',
        }}
      >
        <Text
          strong
          style={{ fontSize: '18px', cursor: 'pointer' }}
          onClick={() => navigate('/lobby')}
        >
          Take That Hill
        </Text>

        <Space>
          {playerId ? (
            <>
              <Space>
                <UserOutlined />
                <Text>{playerName}</Text>
                <Tag color="blue" style={{ fontSize: '10px' }}>
                  {playerId.slice(0, 8)}...
                </Tag>
              </Space>
              <Button size="small" onClick={handleLogout}>
                Logout
              </Button>
            </>
          ) : (
            <Button
              type="primary"
              icon={<LoginOutlined />}
              onClick={() => setIsModalOpen(true)}
            >
              Register
            </Button>
          )}
        </Space>
      </Header>

      <Modal
        title="Welcome to Take That Hill!"
        open={isModalOpen}
        onOk={handleCreatePlayer}
        onCancel={() => playerId && setIsModalOpen(false)}
        okText="Start Playing"
        cancelButtonProps={{ style: { display: playerId ? 'inline-block' : 'none' } }}
        closable={!!playerId}
        maskClosable={!!playerId}
        confirmLoading={isLoading}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Text>Enter your display name to start playing:</Text>
          <Input
            placeholder="Your display name"
            value={inputName}
            onChange={(e) => setInputName(e.target.value)}
            onPressEnter={handleCreatePlayer}
            maxLength={20}
            showCount
          />
        </Space>
      </Modal>
    </>
  );
};

export default AppHeader;
